import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TableSortLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
} from 'recharts';
import { statsApi } from '../services/api';
import { SourcesAnalytics as SourcesAnalyticsType } from '../types/api';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1', '#f57c00', '#7b1fa2'];

type SortOrder = 'asc' | 'desc';

const SourcesAnalytics: React.FC = () => {
  const [period, setPeriod] = useState<number | null>(null);
  const includeBlocked = true; // Всегда считаем с заблокированными

  // Лимиты для отображения записей в таблицах
  const [engagementLimit, setEngagementLimit] = useState(50);
  const [monetizationLimit, setMonetizationLimit] = useState(50);
  const [conversionSpeedLimit, setConversionSpeedLimit] = useState(50);

  // Сортировка для таблиц
  const [engagementSort, setEngagementSort] = useState<{ field: string; order: SortOrder }>({ field: 'total_users', order: 'desc' });
  const [monetizationSort, setMonetizationSort] = useState<{ field: string; order: SortOrder }>({ field: 'paying_users', order: 'desc' });
  const [conversionSpeedSort, setConversionSpeedSort] = useState<{ field: string; order: SortOrder }>({ field: 'users_with_purchase', order: 'desc' });

  const { data, isLoading, error } = useQuery<SourcesAnalyticsType>({
    queryKey: ['sources-analytics', period, includeBlocked],
    queryFn: () => statsApi.getSourcesAnalytics(period, includeBlocked),
    refetchInterval: 60000,
  });

  const handlePeriodChange = (event: SelectChangeEvent<number | string>) => {
    const value = event.target.value;
    setPeriod(value === 'all' ? null : Number(value));
  };

  // Функция сортировки для engagement
  const handleEngagementSort = (field: string) => {
    setEngagementSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Функция сортировки для monetization
  const handleMonetizationSort = (field: string) => {
    setMonetizationSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Функция сортировки для conversion speed
  const handleConversionSpeedSort = (field: string) => {
    setConversionSpeedSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Ошибка загрузки данных: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  const { distribution, engagement, monetization, conversion_speed, timeline, summary } = data;

  // Топ-5 источников для графиков
  const topSources = distribution.slice(0, 5);

  // Объединяем данные engagement и monetization для отображения конверсии в платящих
  const monetizationMap = new Map(monetization.map(m => [m.source, m.payment_conversion]));
  const engagementWithPayment = engagement.map(eng => ({
    ...eng,
    payment_conversion: monetizationMap.get(eng.source) || 0
  }));

  // Применяем сортировку к данным
  const sortedEngagement = [...engagementWithPayment].sort((a, b) => {
    const aVal = a[engagementSort.field as keyof typeof a];
    const bVal = b[engagementSort.field as keyof typeof b];
    const multiplier = engagementSort.order === 'asc' ? 1 : -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return multiplier * aVal.localeCompare(bVal);
    }
    return multiplier * ((aVal as number) - (bVal as number));
  });

  const sortedMonetization = [...monetization].sort((a, b) => {
    const aVal = a[monetizationSort.field as keyof typeof a];
    const bVal = b[monetizationSort.field as keyof typeof b];
    const multiplier = monetizationSort.order === 'asc' ? 1 : -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return multiplier * aVal.localeCompare(bVal);
    }
    return multiplier * ((aVal as number) - (bVal as number));
  });

  const sortedConversionSpeed = [...conversion_speed].sort((a, b) => {
    const aVal = a[conversionSpeedSort.field as keyof typeof a];
    const bVal = b[conversionSpeedSort.field as keyof typeof b];
    const multiplier = conversionSpeedSort.order === 'asc' ? 1 : -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return multiplier * aVal.localeCompare(bVal);
    }
    return multiplier * ((aVal as number) - (bVal as number));
  });

  return (
    <Box>
      {/* Заголовок и контролы */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Источники заходов</Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={period ?? 'all'} onChange={handlePeriodChange}>
            <MenuItem value="all">Всё время</MenuItem>
            <MenuItem value={1}>Сегодня</MenuItem>
            <MenuItem value={7}>Неделя</MenuItem>
            <MenuItem value={30}>Месяц</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Сводка */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Всего пользователей
              </Typography>
              <Typography variant="h4">{summary.total_users.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Источников
              </Typography>
              <Typography variant="h4">{summary.total_sources}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Период
              </Typography>
              <Typography variant="h6">{summary.period}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Распределение пользователей */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Распределение пользователей по источникам
              </Typography>
              <Box sx={{ height: 350, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={distribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="users_count"
                      label={({ percentage }) => `${percentage.toFixed(0)}%`}
                    >
                      {distribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string, entry: any) => [
                        `${value} пользователей (${entry.payload.percentage.toFixed(1)}%)`,
                        entry.payload.source
                      ]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value, entry: any) => `${entry.payload.source}: ${entry.payload.users_count}`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Временная динамика */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Динамика регистраций (топ-5 источников)
              </Typography>
              <Box sx={{ height: 350, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {topSources.map((source, index) => (
                      <Line
                        key={source.source}
                        type="monotone"
                        dataKey={source.source}
                        stroke={COLORS[index % COLORS.length]}
                        name={source.source}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Качество источников (Engagement) */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Качество источников (вовлеченность)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ maxWidth: '200px' }}>
                    <TableSortLabel
                      active={engagementSort.field === 'source'}
                      direction={engagementSort.field === 'source' ? engagementSort.order : 'asc'}
                      onClick={() => handleEngagementSort('source')}
                    >
                      <strong>Источник</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={engagementSort.field === 'total_users'}
                      direction={engagementSort.field === 'total_users' ? engagementSort.order : 'asc'}
                      onClick={() => handleEngagementSort('total_users')}
                    >
                      <strong>Всего</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={engagementSort.field === 'active_users'}
                      direction={engagementSort.field === 'active_users' ? engagementSort.order : 'asc'}
                      onClick={() => handleEngagementSort('active_users')}
                    >
                      <strong>Активных</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={engagementSort.field === 'conversion_rate'}
                      direction={engagementSort.field === 'conversion_rate' ? engagementSort.order : 'asc'}
                      onClick={() => handleEngagementSort('conversion_rate')}
                    >
                      <strong>Конверсия в активных</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={engagementSort.field === 'payment_conversion'}
                      direction={engagementSort.field === 'payment_conversion' ? engagementSort.order : 'asc'}
                      onClick={() => handleEngagementSort('payment_conversion')}
                    >
                      <strong>Конверсия в платящих</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={engagementSort.field === 'avg_sessions_per_user'}
                      direction={engagementSort.field === 'avg_sessions_per_user' ? engagementSort.order : 'asc'}
                      onClick={() => handleEngagementSort('avg_sessions_per_user')}
                    >
                      <strong>Ср. сессий</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={engagementSort.field === 'blocked_rate'}
                      direction={engagementSort.field === 'blocked_rate' ? engagementSort.order : 'asc'}
                      onClick={() => handleEngagementSort('blocked_rate')}
                    >
                      <strong>Заблокировали</strong>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEngagement.slice(0, engagementLimit).map((row) => (
                  <TableRow key={row.source}>
                    <TableCell
                      sx={{
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={row.source}
                    >
                      {row.source}
                    </TableCell>
                    <TableCell align="right">{row.total_users.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.active_users.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.conversion_rate}%`}
                        color={row.conversion_rate >= 50 ? 'success' : row.conversion_rate >= 25 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.payment_conversion}%`}
                        color={row.payment_conversion >= 10 ? 'success' : row.payment_conversion >= 5 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{row.avg_sessions_per_user}</TableCell>
                    <TableCell align="right">
                      {row.blocked_count} ({row.blocked_rate}%)
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedEngagement.length > engagementLimit && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setEngagementLimit(sortedEngagement.length)}
              >
                Показать все ({sortedEngagement.length - engagementLimit} скрыто)
              </Button>
            </Box>
          )}
          {engagementLimit > 50 && sortedEngagement.length > 50 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setEngagementLimit(50)}
              >
                Показать меньше
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Монетизация */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Монетизация по источникам
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ maxWidth: '200px' }}>
                    <TableSortLabel
                      active={monetizationSort.field === 'source'}
                      direction={monetizationSort.field === 'source' ? monetizationSort.order : 'asc'}
                      onClick={() => handleMonetizationSort('source')}
                    >
                      <strong>Источник</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={monetizationSort.field === 'paying_users'}
                      direction={monetizationSort.field === 'paying_users' ? monetizationSort.order : 'asc'}
                      onClick={() => handleMonetizationSort('paying_users')}
                    >
                      <strong>Платящих</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={monetizationSort.field === 'payment_conversion'}
                      direction={monetizationSort.field === 'payment_conversion' ? monetizationSort.order : 'asc'}
                      onClick={() => handleMonetizationSort('payment_conversion')}
                    >
                      <strong>Конверсия в платящих</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={monetizationSort.field === 'total_payments'}
                      direction={monetizationSort.field === 'total_payments' ? monetizationSort.order : 'asc'}
                      onClick={() => handleMonetizationSort('total_payments')}
                    >
                      <strong>Всего платежей</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={monetizationSort.field === 'total_revenue'}
                      direction={monetizationSort.field === 'total_revenue' ? monetizationSort.order : 'asc'}
                      onClick={() => handleMonetizationSort('total_revenue')}
                    >
                      <strong>Общая выручка</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={monetizationSort.field === 'avg_payment'}
                      direction={monetizationSort.field === 'avg_payment' ? monetizationSort.order : 'asc'}
                      onClick={() => handleMonetizationSort('avg_payment')}
                    >
                      <strong>Ср. чек</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={monetizationSort.field === 'avg_payments_per_paying_user'}
                      direction={monetizationSort.field === 'avg_payments_per_paying_user' ? monetizationSort.order : 'asc'}
                      onClick={() => handleMonetizationSort('avg_payments_per_paying_user')}
                    >
                      <strong>Ср. платежей/польз.</strong>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedMonetization.slice(0, monetizationLimit).map((row) => (
                  <TableRow key={row.source}>
                    <TableCell
                      sx={{
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={row.source}
                    >
                      {row.source}
                    </TableCell>
                    <TableCell align="right">{row.paying_users.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.payment_conversion}%`}
                        color={row.payment_conversion >= 10 ? 'success' : row.payment_conversion >= 5 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{row.total_payments.toLocaleString()}</TableCell>
                    <TableCell align="right">${row.total_revenue.toLocaleString()}</TableCell>
                    <TableCell align="right">${row.avg_payment}</TableCell>
                    <TableCell align="right">{row.avg_payments_per_paying_user}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedMonetization.length > monetizationLimit && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setMonetizationLimit(sortedMonetization.length)}
              >
                Показать все ({sortedMonetization.length - monetizationLimit} скрыто)
              </Button>
            </Box>
          )}
          {monetizationLimit > 50 && sortedMonetization.length > 50 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setMonetizationLimit(50)}
              >
                Показать меньше
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Скорость конверсии (время до первой покупки) */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Скорость конверсии (время от регистрации до первой покупки)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ maxWidth: '200px' }}>
                    <TableSortLabel
                      active={conversionSpeedSort.field === 'source'}
                      direction={conversionSpeedSort.field === 'source' ? conversionSpeedSort.order : 'asc'}
                      onClick={() => handleConversionSpeedSort('source')}
                    >
                      <strong>Источник</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={conversionSpeedSort.field === 'users_with_purchase'}
                      direction={conversionSpeedSort.field === 'users_with_purchase' ? conversionSpeedSort.order : 'asc'}
                      onClick={() => handleConversionSpeedSort('users_with_purchase')}
                    >
                      <strong>Купивших</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={conversionSpeedSort.field === 'avg_time_days'}
                      direction={conversionSpeedSort.field === 'avg_time_days' ? conversionSpeedSort.order : 'asc'}
                      onClick={() => handleConversionSpeedSort('avg_time_days')}
                    >
                      <strong>Среднее время</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={conversionSpeedSort.field === 'median_time_days'}
                      direction={conversionSpeedSort.field === 'median_time_days' ? conversionSpeedSort.order : 'asc'}
                      onClick={() => handleConversionSpeedSort('median_time_days')}
                    >
                      <strong>Медиана</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={conversionSpeedSort.field === 'min_time_hours'}
                      direction={conversionSpeedSort.field === 'min_time_hours' ? conversionSpeedSort.order : 'asc'}
                      onClick={() => handleConversionSpeedSort('min_time_hours')}
                    >
                      <strong>Минимум</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={conversionSpeedSort.field === 'max_time_hours'}
                      direction={conversionSpeedSort.field === 'max_time_hours' ? conversionSpeedSort.order : 'asc'}
                      onClick={() => handleConversionSpeedSort('max_time_hours')}
                    >
                      <strong>Максимум</strong>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedConversionSpeed.slice(0, conversionSpeedLimit).map((row) => {
                  const formatTime = (days: number, hours: number) => {
                    if (days >= 1) {
                      return `${days.toFixed(1)} дн.`;
                    } else if (hours >= 1) {
                      return `${hours.toFixed(1)} ч.`;
                    } else {
                      return `${(hours * 60).toFixed(0)} мин.`;
                    }
                  };

                  const getTimeColor = (days: number) => {
                    if (days <= 1) return 'success';
                    if (days <= 7) return 'warning';
                    return 'default';
                  };

                  return (
                    <TableRow key={row.source}>
                      <TableCell
                        sx={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={row.source}
                      >
                        {row.source}
                      </TableCell>
                      <TableCell align="right">{row.users_with_purchase.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {row.users_with_purchase > 0 ? (
                          <Chip
                            label={formatTime(row.avg_time_days, row.avg_time_hours)}
                            color={getTimeColor(row.avg_time_days)}
                            size="small"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.users_with_purchase > 0 ? formatTime(row.median_time_days, row.median_time_hours) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {row.users_with_purchase > 0 ? `${row.min_time_hours.toFixed(1)} ч.` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {row.users_with_purchase > 0 ? formatTime(row.max_time_hours / 24, row.max_time_hours) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedConversionSpeed.length > conversionSpeedLimit && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setConversionSpeedLimit(sortedConversionSpeed.length)}
              >
                Показать все ({sortedConversionSpeed.length - conversionSpeedLimit} скрыто)
              </Button>
            </Box>
          )}
          {conversionSpeedLimit > 50 && sortedConversionSpeed.length > 50 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setConversionSpeedLimit(50)}
              >
                Показать меньше
              </Button>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Пояснение:</strong> Показано время от момента регистрации пользователя до его первой завершённой покупки.
              Зелёным выделено время до 1 дня, жёлтым - до 7 дней.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SourcesAnalytics;
